/**
 * API Service
 *
 * Centralized API client. Auth = session cookie set by the backend (uniconnect_session).
 * All requests send `credentials: 'include'` so the browser carries the cookie.
 * On 401 we navigate to the backend's /auth/login, preserving the current path via ?next.
 */

import type {
  OnlyPersonResponse,
  CompetenciesResponse,
  ProjectsResponse,
  PersonSuggestionsResponse,
  PeopleLandingResponse,
  CollaborationGraphResponse,
  CollaboratorsResponse,
  SharedProjectsResponse,
  UniversityGraphResponse,
  FacultyGraphResponse,
  CrossFacultyProjectsResponse,
  SuggestionsResponse,
  ProjectSearchResponse,
  ProjectSuggestionsResponse,
  ProjectDetailResponse,
  ProjectsLandingResponse,
  SimilarProjectsResponse,
  CompanyListResponse,
  CompanySuggestionsResponse,
  CompanyDetailResponse,
  SearchResponse,
  MatchedResearchersResponse,
  DashboardData,
  AlumniListResponse,
  AlumniCommentsResponse,
  AlumniComment,
  CtuCompaniesResponse,
  AdminStats,
  AdminLogsResponse,
  AdminLogsParams,
} from '../types/api';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// =============================================================================
// Auth helpers (cookie-session via backend /auth/*)
// =============================================================================

export type MeResponse = {
  oid?: string | null;
  sub?: string | null;
  uid_number?: string | null;
  username?: string | null;
  upn?: string | null;
  name?: string | null;
  email?: string | null;
  roles?: string[] | null;
};

export const buildLoginUrl = (nextPath: string): string =>
  `${API_BASE_URL}/auth/login?next=${encodeURIComponent(nextPath)}`;

function redirectToLogin(): void {
  window.location.href = buildLoginUrl(window.location.pathname + window.location.search);
}

function redirectToAccessDenied(): void {
  if (window.location.pathname !== '/access-denied') {
    window.location.href = '/access-denied';
  }
}

// =============================================================================
// Request Helper
// =============================================================================

interface RequestOptions {
  method?: string;
  headers?: Record<string, string>;
  body?: any;
  responseType?: string;
  // Skip the auto-redirect to /auth/login on 401. Used by getCurrentUser()
  // so the AuthProvider can detect signed-out state without bouncing the page.
  suppressAuthRedirect?: boolean;
}

async function request<T = any>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  const config: RequestInit = {
    method: options.method || 'GET',
    headers,
    credentials: 'include',
  };

  if (options.body && typeof options.body === 'object') {
    config.body = JSON.stringify(options.body);
  }

  try {
    const response = await fetch(url, config);

    if (response.status === 401) {
      if (!options.suppressAuthRedirect) {
        redirectToLogin();
      }
      throw new Error('Authentication required. Please log in again.');
    }

    if (response.status === 403) {
      // Authenticated but lacks the required role (e.g. not ADMINISTRATOR).
      // Send the user to the explanatory page instead of a wall of errors.
      console.error('Authorization error - access denied');
      redirectToAccessDenied();
      throw new Error('Access denied. You do not have permission to access this resource.');
    }

    if (!response.ok) {
      let errorMsg = `API request failed: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.detail) errorMsg += ` - ${errorData.detail}`;
        else if (errorData.error) errorMsg += ` - ${errorData.error}`;
      } catch (e) {
        // Ignore if error response is not JSON
      }
      throw new Error(errorMsg);
    }

    if (response.status === 204 || options.responseType === 'text') {
      return response.text() as unknown as T;
    }

    return response.json() as Promise<T>;
  } catch (error) {
    console.error(`API request error to ${endpoint}:`, error);
    throw error;
  }
}

// =============================================================================
// API Endpoints
// =============================================================================

// Person related API calls
export const getPersonDetails = (id: number | string): Promise<OnlyPersonResponse> =>
  request(`/person?id=${encodeURIComponent(id)}`);
export const getPersonCompetencies = (personId: number | string): Promise<CompetenciesResponse> =>
  request(`/competencies?id_person=${encodeURIComponent(personId)}`);
export const getPersonProjects = (personId: number | string): Promise<ProjectsResponse> =>
  request(`/projects?id_person=${encodeURIComponent(personId)}`);
export const getPersonSuggestions = (query: string): Promise<PersonSuggestionsResponse> =>
  request('/person_suggestions', { method: 'POST', body: { query } });
export const getKeywordSuggestions = (query: string): Promise<{ suggestions: string[]; error?: string }> =>
  request('/keyword_suggestions', { method: 'POST', body: { query } });
export const getPeopleLanding = (featuredIds: number[]): Promise<PeopleLandingResponse> =>
  request(`/people/landing?featured=${encodeURIComponent(featuredIds.join(','))}`);
export const getPersonCollaborationGraph = (personId: number | string): Promise<CollaborationGraphResponse> =>
  request(`/person/collaboration-graph?id_person=${encodeURIComponent(personId)}`);
export const getPersonCollaborators = (personId: number | string): Promise<CollaboratorsResponse> =>
  request(`/person/collaborators?id_person=${encodeURIComponent(personId)}`);
export const getSharedProjects = (personA: number | string, personB: number | string): Promise<SharedProjectsResponse> =>
  request(`/person/shared-projects?person_a=${encodeURIComponent(personA)}&person_b=${encodeURIComponent(personB)}`);

// Network (university collaboration graph) API calls
export const getUniversityGraph = (): Promise<UniversityGraphResponse> =>
  request('/network/faculties');
export const getFacultyGraph = (abbreviation: string): Promise<FacultyGraphResponse> =>
  request(`/network/faculty-graph?abbreviation=${encodeURIComponent(abbreviation)}`);
export const getCrossFacultyProjects = (facultyA: string, facultyB: string): Promise<CrossFacultyProjectsResponse> =>
  request(`/network/cross-faculty-projects?faculty_a=${encodeURIComponent(facultyA)}&faculty_b=${encodeURIComponent(facultyB)}`);

// Project related API calls
export const searchResults = (
  keywords: string[] = [],
  operators: string[] = [],
  faculties: string[] = [],
  yearFrom?: number,
  yearTo?: number,
  types: string[] = [],
): Promise<ProjectSearchResponse> => {
  const body: {
    keywords: { text: string }[];
    operators: string[];
    faculties?: string[];
    year_from?: number;
    year_to?: number;
    types?: string[];
  } = {
    keywords: keywords.map((text) => ({ text })),
    operators,
  };
  if (faculties.length > 0) body.faculties = faculties;
  if (yearFrom !== undefined) body.year_from = yearFrom;
  if (yearTo !== undefined) body.year_to = yearTo;
  if (types.length > 0) body.types = types;
  return request('/results/search', { method: 'POST', body });
};
export const getProjectSuggestions = (query: string): Promise<ProjectSuggestionsResponse> =>
  request('/outcomes/suggestions', { method: 'POST', body: { query } });
export const getProjectDetails = (id: number | string): Promise<ProjectDetailResponse> =>
  request(`/outcome/detail?id=${encodeURIComponent(id)}`);
export const getProjectsLanding = (): Promise<ProjectsLandingResponse> =>
  request('/outcomes/landing');
export const getSimilarProjects = (
  id: number | string,
  limit = 50,
): Promise<SimilarProjectsResponse> =>
  request(`/outcome/similar?id=${encodeURIComponent(id)}&limit=${limit}`);

// Company related API calls
export const searchCompanies = (keywords: string[] = [], operators: string[] = []): Promise<CompanyListResponse> => {
  const body = {
    keywords: keywords.map((text) => ({ text })),
    operators,
  };
  return request('/companies/search', { method: 'POST', body });
};
export const getCompanySuggestions = (query: string): Promise<CompanySuggestionsResponse> =>
  request('/company_suggestions', { method: 'POST', body: { query } });
export const getCompanyDetails = (id: number | string, lang: string): Promise<CompanyDetailResponse> =>
  request(`/company?id=${encodeURIComponent(id)}&lang=${encodeURIComponent(lang)}`);
export const getAllCompanies = (): Promise<CompanyListResponse> =>
  request('/companies/all');

// Search API
export const searchExperts = (
  keywords: string[] = [],
  operators: string[] = [],
  faculties: string[] = [],
  yearFrom?: number,
  yearTo?: number,
  limit?: number,
  offset?: number,
): Promise<SearchResponse> => {
  const body: {
    keywords: { text: string }[];
    operators: string[];
    faculties?: string[];
    year_from?: number;
    year_to?: number;
    limit?: number;
    offset?: number;
  } = {
    keywords: keywords.map((text) => ({ text })),
    operators,
  };
  if (faculties.length > 0) {
    body.faculties = faculties;
  }
  if (yearFrom !== undefined) {
    body.year_from = yearFrom;
  }
  if (yearTo !== undefined) {
    body.year_to = yearTo;
  }
  if (limit !== undefined) {
    body.limit = limit;
  }
  if (offset !== undefined) {
    body.offset = offset;
  }
  return request('/search', { method: 'POST', body });
};

// Alumni
export const getAllAlumni = (): Promise<AlumniListResponse> => request('/alumni');
export const toggleAlumniStar = (alumniId: number | string): Promise<any> =>
  request(`/alumni/${encodeURIComponent(alumniId)}/star`, { method: 'POST' });
export const getAlumniComments = (alumniId: number | string): Promise<AlumniCommentsResponse> =>
  request(`/alumni/${encodeURIComponent(alumniId)}/comments`);
// Author name is derived server-side from the session — never sent by the client.
export const createAlumniComment = (alumniId: number | string, commentText: string): Promise<AlumniComment> =>
  request(`/alumni/${encodeURIComponent(alumniId)}/comments`, { method: 'POST', body: { comment_text: commentText } });
export const updateAlumniComment = (commentId: number | string, commentText: string): Promise<AlumniComment> =>
  request(`/alumni/comments/${encodeURIComponent(commentId)}`, { method: 'PUT', body: { comment_text: commentText } });
export const deleteAlumniComment = (commentId: number | string): Promise<void> =>
  request(`/alumni/comments/${encodeURIComponent(commentId)}`, { method: 'DELETE' });

// Dashboard
export const getDashboardData = (): Promise<DashboardData> => request('/dashboard/data');

// Projects list for a person
export const getProjectsList = (personId: number | string): Promise<ProjectsResponse> =>
  request(`/projects/list_projects?id_person=${encodeURIComponent(personId)}`);

// Company-Researcher matching
export const getCompanyResearchers = (companyId: number | string): Promise<MatchedResearchersResponse> =>
  request(`/company/${encodeURIComponent(companyId)}/researchers`);

// CTU Companies (spin-offs / start-ups)
export const getCtuCompanies = (): Promise<CtuCompaniesResponse> =>
  request('/ctu-companies');

// Admin (ADMINISTRATOR-only; backend enforces the role, a 403 redirects to /access-denied)
export const getAdminStats = (days = 30): Promise<AdminStats> =>
  request(`/admin/stats?days=${encodeURIComponent(days)}`);

export const getAdminLogs = (params: AdminLogsParams = {}): Promise<AdminLogsResponse> => {
  const qs = new URLSearchParams();
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  if (params.category) qs.set('category', params.category);
  if (params.q) qs.set('q', params.q);
  const query = qs.toString();
  return request(`/admin/logs${query ? `?${query}` : ''}`);
};

// Auth
export const getCurrentUser = (): Promise<MeResponse> =>
  request('/auth/me', { suppressAuthRedirect: true });
export const logout = (): Promise<{ logout_url: string }> =>
  request('/auth/logout', { method: 'POST' });
